import React from 'react';

interface Tab {
    id: string;
    label: string;
    disabled?: boolean;
    icon?: React.ReactNode;
    badge?: string | number;
}

interface TabsProps {
    tabs: Tab[];
    activeTab: string;
    setActiveTab: (tabId: string) => void;
    children: React.ReactNode;
    variant?: 'default' | 'pills' | 'underline' | 'enclosed';
    size?: 'small' | 'medium' | 'large';
    className?: string;
    fullWidth?: boolean;
}

const Tabs: React.FC<TabsProps> = ({
    tabs,
    activeTab,
    setActiveTab,
    children,
    variant = 'default',
    size = 'medium',
    className = '',
    fullWidth = false
}) => {
    const sizes = {
        small: 'text-[13px] px-4 py-2',
        medium: 'text-sm px-5 py-2.5',
        large: 'text-[15px] px-6 py-3.5'
    };

    const variants = {
        default: {
            container: 'border-b border-[var(--border-subtle)]',
            nav: '-mb-px flex gap-1',
            tab: (isActive: boolean, disabled: boolean) => `
                border-b-2 font-semibold tracking-[-0.008em]
                transition-all duration-[var(--duration-normal)] ease-[var(--ease-calm)]
                ${isActive
                    ? 'border-[var(--primary-500)] text-[var(--primary-500)] bg-[var(--primary-50)]'
                    : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-secondary)] hover:border-[var(--border-glass)] hover:bg-[var(--bg-glass)]'
                }
                ${disabled ? 'cursor-not-allowed opacity-35' : 'cursor-pointer'}
            `
        },
        pills: {
            container: '',
            nav: 'flex gap-1.5 p-1.5 rounded-[var(--radius-lg)] bg-[var(--bg-surface)] border border-[var(--border-subtle)]',
            tab: (isActive: boolean, disabled: boolean) => `
                rounded-[var(--radius-md)] font-semibold tracking-[-0.008em]
                transition-all duration-[var(--duration-normal)] ease-[var(--ease-calm)]
                ${isActive
                    ? 'bg-gradient-to-b from-[var(--primary-500)] to-[var(--primary-600)] text-white shadow-[0_1px_4px_rgba(4,94,184,0.28),inset_0_1px_0_rgba(255,255,255,0.10)]'
                    : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)]'
                }
                ${disabled ? 'cursor-not-allowed opacity-35' : 'cursor-pointer'}
            `
        },
        underline: {
            container: 'border-b border-[var(--border-subtle)]',
            nav: 'flex gap-7',
            tab: (isActive: boolean, disabled: boolean) => `
                pb-3 border-b-2 font-semibold tracking-[-0.008em]
                transition-all duration-[var(--duration-normal)] ease-[var(--ease-calm)]
                ${isActive
                    ? 'border-[var(--primary-500)] text-[var(--primary-500)]'
                    : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-secondary)] hover:border-[var(--border-glass)]'
                }
                ${disabled ? 'cursor-not-allowed opacity-35' : 'cursor-pointer'}
            `
        },
        enclosed: {
            container: 'border-b border-[var(--border-subtle)]',
            nav: '-mb-px flex gap-1',
            tab: (isActive: boolean, disabled: boolean) => `
                border border-[var(--border-subtle)] rounded-t-[var(--radius-md)] font-semibold tracking-[-0.008em]
                transition-all duration-[var(--duration-normal)] ease-[var(--ease-calm)]
                ${isActive
                    ? 'bg-[var(--bg-card)] border-b-[var(--bg-card)] text-[var(--text-primary)] shadow-[var(--shadow-sm)]'
                    : 'bg-[var(--bg-surface)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]'
                }
                ${disabled ? 'cursor-not-allowed opacity-35' : 'cursor-pointer'}
            `
        }
    };

    const currentVariant = variants[variant];

    return (
        <div className={className}>
            <div className={currentVariant.container}>
                <nav className={`${currentVariant.nav} ${fullWidth ? 'w-full' : ''}`} role="tablist">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            role="tab"
                            aria-selected={activeTab === tab.id}
                            disabled={tab.disabled}
                            onClick={() => !tab.disabled && setActiveTab(tab.id)}
                            className={`
                                ${sizes[size]}
                                ${currentVariant.tab(activeTab === tab.id, !!tab.disabled)}
                                ${fullWidth ? 'flex-1' : ''}
                                flex items-center gap-2 justify-center
                                focus:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--primary-500)]/20
                                focus-visible:ring-offset-1 focus-visible:ring-offset-[var(--bg-main)] rounded-[var(--radius-sm)]
                            `}
                        >
                            {tab.icon && (
                                <span className={`${size === 'large' ? 'w-5 h-5' : 'w-4 h-4'} opacity-80`}>
                                    {tab.icon}
                                </span>
                            )}
                            <span>{tab.label}</span>
                            {tab.badge && (
                                <span className={`
                                    inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-medium
                                    rounded-full min-w-[1.25rem] h-5
                                    ${activeTab === tab.id
                                        ? 'bg-white/20 text-current'
                                        : 'bg-[var(--bg-hover)] text-[var(--text-secondary)]'
                                    }
                                `}>
                                    {tab.badge}
                                </span>
                            )}
                        </button>
                    ))}
                </nav>
            </div>
            <div className={`${variant === 'pills' ? 'mt-5' : 'pt-6'}`}>
                {children}
            </div>
        </div>
    );
};

export default Tabs;
